import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import { api } from '@/utils/api';
import PasswordInput from '@/components/base/PasswordInput';

/**
 * Set a new password from an emailed link (ForgotPasswordController::showResetForm
 * and reset): at least 8 characters, typed twice. Back to sign-in when done.
 */
export default function ResetPassword() {
  // Our API links to /reset-password/<token>; the agency API to /reset-password?token=…&email=….
  const params = useParams();
  const [query] = useSearchParams();
  const token = params.token ?? query.get('token') ?? '';
  const email = query.get('email') ?? '';
  const navigate = useNavigate();
  const [state, setState] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api(`/reset-password/${encodeURIComponent(token)}`)
      .then(() => !cancelled && setState('valid'))
      .catch(() => !cancelled && setState('invalid'));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirmation.length > 0 && confirmation !== password;
  const valid = password.length >= 8 && confirmation === password;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setError('');
    setSaving(true);
    try {
      const reply = await api<{ message: string }>('/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, email, password, passwordConfirmation: confirmation }),
      });
      navigate('/login', { replace: true, state: { notice: reply.message } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset the password.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'mt-1 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="page-rule mx-auto" aria-hidden="true" />
          <h1 className="mt-2 font-heading text-2xl font-semibold text-foreground-950">Set a new password</h1>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          {state === 'checking' ? (
            <p className="text-sm text-foreground-500">Checking your link…</p>
          ) : state === 'invalid' ? (
            <div className="space-y-3 text-sm text-foreground-700">
              <p>This reset link has expired or is invalid.</p>
              <Link to="/forgot-password" className="font-medium text-primary-700 underline">
                Send a new link
              </Link>
            </div>
          ) : (
            <form onSubmit={(event) => void handleSubmit(event)}>
              {error ? (
                <div
                  role="alert"
                  className="mb-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800"
                >
                  <i className="ri-error-warning-line mt-0.5" />
                  {error}
                </div>
              ) : null}
              <label className="block text-sm font-medium text-foreground-800">
                New password
                <PasswordInput
                  autoComplete="new-password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                />
              </label>
              <p className={`mt-1 text-[11px] ${tooShort ? 'text-primary-700' : 'text-foreground-500'}`}>At least 8 characters.</p>
              <label className="mt-4 block text-sm font-medium text-foreground-800">
                Confirm password
                <PasswordInput
                  autoComplete="new-password"
                  required
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  className={inputClass}
                />
              </label>
              {mismatch ? <p className="mt-1 text-[11px] text-primary-700">The passwords don&apos;t match.</p> : null}
              <Button type="submit" size="lg" className="mt-6 w-full" disabled={!valid || saving}>
                {saving ? 'Saving…' : 'Save new password'}
              </Button>
            </form>
          )}
          <div className="mt-4 text-center">
            <Link to="/login" className="text-xs font-medium text-foreground-500 hover:text-foreground-800">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
